import React from 'react';
import Helmet from 'react-helmet';
import Joi from 'joi-browser';
import * as _ from 'lodash';
import { TouchDeviceInvisible } from '../components/TouchDeviceInvisible';
import Information from '../components/modals/common/Information';
import { KeyplanContents } from '../components/tables/Keyplan';
import { FieldImageService } from '../services/FieldImages';
import { validationByJoi } from '../utilities/commonValidation';
import { HEADER_CONSTANTS } from '../utilities/constants';
import ImageUtility from '../utilities/imageUtility';
import { messageUtility } from '../utilities/messageUtility';
import { deleteControlCode } from '../utilities/stringUtil';
import { onceAsyncRequest } from '../utilities/commonUtil';

// ダイアログ名
const DIALOG_NAME = {
  errorfileType: 'errorfileType', // ファイルタイプエラー
  errorfileSize: 'errorfileSize', // ファイルサイズエラー
};

/**
 * @description キープラン画面
 */
export class Keyplan extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      fieldId: sessionStorage.getItem('field_id'),
      keyplans: [],
      canDrop: false, //ドラッグアンドドロップの状態
      dragSort: [], //ドラッグアンドドロップの並び順の仮置き場
      orderParam: {
        order: 'order_number_asc',
      },
      isSettingId: null,
      formValues: {},
      errors: {
        comment: '',
        image_url: '',
      },
      lightboxState: false,
      selectedLightboxImages: [],
      visibleDialogName: null, // 開閉用ダイアログ名
    };

    // 新規登録時は使用中とする
    this.defaultValues = {
      id: null,
      display_flag: 1,
      category_code: 9,
      comment: '',
      file_name: null,
      image_url: '',
      order_number: 0,
    };

    this.events = {
      form: {
        setFormValues: this.setFormValues.bind(this),
        clearFormValues: this.clearFormValues.bind(this),
        set: this.setFlatData.bind(this),
        setImage: this.setImage.bind(this),
        submit: this.eventSubmit.bind(this),
        validate: this.validate.bind(this),
      },
      modal: {
        handleClickThumbnail: this.handleClickThumbnail.bind(this),
        handleCloseLightbox: this.handleCloseLightbox.bind(this),
      },
      //ドラッグアンドドロップ用メソッド
      dnd: {
        onBeginDrag: this.onBeginDrag.bind(this),
        onDropped: this.onDropped.bind(this),
        draggable: this.draggable.bind(this),
        orderSubmit: this.eventOrderSubmit.bind(this),
      },
    };
    this.handleVisibleDialog = this.handleVisibleDialog.bind(this); // ダイアログ開閉設定

    this.schema = Joi.object().keys({
      id: Joi.allow(),
      display_flag: Joi.number().required(),
      category_code: Joi.number().required(),
      comment: Joi.string()
        .max(512)
        .required(),
      file_name: Joi.allow(),
      image_url: Joi.string().required(),
      order_number: Joi.allow(),
    });

    this.keyplanCategoryCodes = [
      1,
      8,
      9,
      10,
      11,
      18,
      19,
      20,
      21,
      22,
      23,
      24,
      25,
      26,
      27,
      28,
      29,
      30,
      31,
      32,
    ];
  }

  componentDidMount() {
    this.setKeyplans();
  }

  async setKeyplans() {
    const fieldId = this.state.fieldId;
    await FieldImageService.keyplan(fieldId, this.state.orderParam).then(
      resp => {
        if (resp.result) {
          const fieldImages = resp.data.entities.field_images;
          this.setState({
            keyplans: fieldImages,
          });
        }
      }
    );
  }

  setFormValues = (keyplan, index) => {
    const formValues = {};
    let errMsg = '';
    _.each(Object.keys(this.defaultValues), key => {
      formValues[key] = keyplan[key];
    });
    if (!formValues.id) {
      const usedCategoryCodes = _.map(this.state.keyplans, 'category_code');
      const useCategoryCodes = _.difference(
        this.keyplanCategoryCodes,
        usedCategoryCodes
      );
      if (useCategoryCodes.length === 0) {
        errMsg = 'キープランの登録は20件までです。';
      } else {
        formValues.category_code = useCategoryCodes[0];
      }
    }
    formValues.order_number = index;
    if (errMsg === '') {
      this.setState({
        isSettingId: keyplan.id ? keyplan.id : 0,
        formValues,
        errors: {},
      });
    } else {
      this.setState({
        errors: {
          register_error: errMsg,
        },
      });
    }
  };

  clearFormValues = () => {
    this.setState({
      isSettingId: null,
      formValues: this.defaultValues,
      errors: {},
    });
  };

  /**
   * ドラッグ開始
   */
  onBeginDrag(props) {
    let dragSort = _.cloneDeep(this.state.dragSort);
    dragSort[0] = props.index;
    this.setState({ dragSort });
  }

  /**
   * ドロップダウン
   */
  onDropped(dragProps, dropProps) {
    let dragSort = _.cloneDeep(this.state.dragSort);
    dragSort[1] = dropProps.index;
    this.setState({ dragSort });
    this.sortList(...this.state.dragSort);
  }

  /**
   * ドラッグ開始、終了
   */
  draggable(bool) {
    this.setState({ canDrag: bool });
  }

  /**
   * @description 並び順を変更する
   */
  sortList(dragIndex, to_index) {
    const lists = _.cloneDeep(this.state.keyplans);
    const formItem = lists[dragIndex];
    if (to_index < dragIndex) {
      lists.splice(to_index, 0, formItem);
      lists.splice(dragIndex + 1, 1);
    } else {
      lists.splice(to_index + 1, 0, formItem);
      lists.splice(dragIndex, 1);
    }
    this.setState({ keyplans: lists });
  }

  setFlatData = e => {
    const formValues = _.cloneDeep(this.state.formValues);
    formValues[e.target.name] = deleteControlCode(e.target.value);
    this.setState({ formValues });
  };

  /**
   * @description 選択ファイル設定
   * @param {*} target 操作ファイル要素
   */
  setImage = async target => {
    if (!target.files[0]) {
      return;
    }

    const isTypeResult = await ImageUtility.fileTypeCheck(target.files[0]);
    const isSizeResult = await ImageUtility.fileSizeCheck(target.files[0]);
    if (!isTypeResult) {
      target.value = '';
      return this.handleVisibleDialog(DIALOG_NAME.errorfileType);
    }
    if (!isSizeResult) {
      target.value = '';
      return this.handleVisibleDialog(DIALOG_NAME.errorfileSize);
    }

    const formValues = { ...this.state.formValues };
    const errors = { ...this.state.errors };
    const createObjectURL =
      (window.URL || window.webkitURL).createObjectURL ||
      window.createObjectURL;
    delete errors.image_url;
    formValues.image_url = createObjectURL(target.files[0]);
    formValues.file_name = target.files[0];
    this.setState({ formValues, errors });
  };

  /**
   * @description ダイアログ開閉設定
   * @param {boolean} visibleDialogName ダイアログ名 ※nullの場合は、ダイアログを閉じる
   */
  handleVisibleDialog(visibleDialogName) {
    this.setState({ visibleDialogName });
  }

  validate(e) {
    const params = this.state.formValues;
    let result = validationByJoi(this.schema, params);
    let errors = this.state.errors;
    //単数or複数判定
    if (e) {
      if (result.messages[e.target.name]) {
        errors[e.target.name] = result.messages[e.target.name];
      } else if (errors[e.target.name]) {
        delete errors[e.target.name];
      }
    } else {
      if (!result.status) {
        _.each(result.messages, (value, key) => {
          errors[key] = value;
        });
      }
    }
    this.setState({ errors });
    return Object.keys(result.messages).length;
  }

  eventSubmit = onceAsyncRequest(async () => {
    if (this.validate() !== 0) {
      return;
    }
    const params = this.state.formValues;
    const fieldId = this.state.fieldId;
    if (params.id && typeof params.file_name !== 'object') {
      delete params.file_name;
      await FieldImageService.update(fieldId, params.id, params).then(
        async resp => {
          if (resp.result) {
            await this.setKeyplans();
            this.setState({
              isSettingId: null,
              errors: {},
            });
          }
        }
      );
    } else {
      const uploadParam = {
        category_code: params.category_code,
      };
      await FieldImageService.upload(fieldId, uploadParam, params).then(
        async resp => {
          if (resp.result) {
            await this.setKeyplans();
            this.setState({
              isSettingId: null,
              errors: {},
            });
          }
        }
      );
    }
  });

  eventOrderSubmit = () => {
    const fieldId = this.state.fieldId;
    const items = this.state.keyplans;
    const params = { ids: _.map(items, 'id') };
    FieldImageService.updateOrder(fieldId, params).then(resp => {
      if (resp.result) {
        this.setKeyplans();
      }
    });
  };

  handleClickThumbnail = keyplan => {
    this.setState({
      lightboxState: true,
      selectedLightboxImages: [{ src: keyplan.image_url }],
    });
  };

  handleCloseLightbox = () => {
    this.setState({
      lightboxState: false,
      selectedLightboxImages: [],
    });
  };

  headerMenu = () => {
    if (this.state.canDrag) {
      return (
        <div className='setting-header__item'>
          <button
            className='btn--effective'
            onClick={() => {
              this.events.dnd.orderSubmit();
              this.events.dnd.draggable(false);
            }}
          >
            保存
          </button>
        </div>
      );
    }
    return (
      <div className='setting-header__item'>
        <button
          className='btn'
          onClick={() => this.events.form.setFormValues(this.defaultValues, 0)}
        >
          新規作成
        </button>
        <div className='validate-message show'>
          {this.state.errors.register_error}
        </div>
        <TouchDeviceInvisible>
          <button
            className='btn--sub'
            onClick={() => this.events.dnd.draggable(true)}
            style={{
              display:
                this.state.searched || this.state.isSettingId !== null
                  ? 'none'
                  : 'inline-block',
            }}
          >
            並び順を変更する
          </button>
        </TouchDeviceInvisible>
      </div>
    );
  };

  render() {
    return (
      <div className='setting-main__body'>
        <Helmet>
          <title>{HEADER_CONSTANTS.conference} - キープラン</title>
        </Helmet>
        <div id='setting-header' className='setting-header'>
          {this.headerMenu()}
        </div>
        <KeyplanContents
          keyplans={this.state.keyplans}
          values={this.state.formValues}
          errors={this.state.errors}
          isSettingId={this.state.isSettingId}
          callbacks={this.events}
          canDrag={this.state.canDrag}
          lightboxState={this.state.lightboxState}
          selectedLightboxImages={this.state.selectedLightboxImages}
        />
        <Information
          visible={this.state.visibleDialogName === DIALOG_NAME.errorfileType}
          title='登録失敗'
          message={messageUtility.getMessage('E-000-012')}
          buttonText={messageUtility.getMessage('I-000-011')}
          onClose={() => this.handleVisibleDialog(null)}
          buttonClassName='btn--sm--sub'
        />
        <Information
          visible={this.state.visibleDialogName === DIALOG_NAME.errorfileSize}
          title='登録失敗'
          message={messageUtility.getMessage('E-000-011')}
          buttonText={messageUtility.getMessage('I-000-011')}
          onClose={() => this.handleVisibleDialog(null)}
          buttonClassName='btn--sm--sub'
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/pages/Keyplan.jsx